import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { List } from '@factorialco/f0-react/icons/app'
import { SupplementsDataDocument } from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { ExpensablesProvider } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import { SupplementExtraDataProvider } from 'modules/payroll_bundle/components/Supplements/supplementExtraDataContext'
import { useSupplementsEmployeesDataCollectionSource } from 'modules/payroll_bundle/components/Supplements/useSupplementsEmployeesDataCollectionSource'
import useFetchData from 'modules/payroll_bundle/components/Supplements/useFetchData'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  SHOW_MANAGED_IMPORTED_MODAL,
  SHOW_UPLOAD_MODAL
} from 'modules/payroll_bundle/lib/constants'
import type { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { StatusAuthorizationProvider } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import { SupplementsInsightsContextProvider } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'
import SupplementsInsights from 'modules/payroll_bundle/pages/SupplementsPage/SupplementsInsights'
import DeleteModal from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_components/DeleteModal'
import TaxonomyForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/CompensationTypePopoverMenu/TaxonomyForm'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { useExpensables } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { useFrequency } from 'modules/payroll_bundle/lib/frequencyContext'
import supplementBulkImportCollection from 'modules/payroll_bundle/store/collections/supplementBulkImports'

import { SupplementsCustomVisualizationByDate } from './SupplementsCustomVisualizationByDate'

export const SupplementsByDateAndFrequency = () => {
  const { legalEntities } = useCurrentSession().company
  const [openTaxonomyCreationModal, toggleTaxonomyCreationModal] = useToggle(false)
  const handleTaxonomyCreationModal = (newState: boolean) => {
    toggleTaxonomyCreationModal(newState)()
  }
  const [employeeExpansionState, setEmployeeExpansionState] = useState<Record<string, boolean>>({})

  const { date, frequency } = useFrequency()
  const supplementsStore = useLazyRef<SupplementsFormStore>(
    () => new SupplementsFormStore()
  ).current

  const { filteredEmployees } = useOutletContext<RouteContextParams>()
  const visibleEmployees: Employee[] = filteredEmployees
  const visibleEmployeesKey = visibleEmployees.map((e) => e.id).join(',')
  const employeesById = useMemo(
    () => Object.fromEntries(visibleEmployees.map((e) => [e.id, e])),
    [visibleEmployeesKey]
  )

  const contractDate = dateToString(date, 'backend')
  const source = useSupplementsEmployeesDataCollectionSource({ contractDate })
  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records ?? []
  const recordsKey = records.map((r: { id: number }) => r.id).join(',')

  const employeeIds = useMemo(() => records.map((r: { id: number }) => r.id), [recordsKey])
  const currentPage = 1

  const { loading } = useFetchData({
    date,
    frequency,
    supplementsStore,
    employeeIds,
    currentPage
  })

  const { data, loading: loadingData } = useQuery(SupplementsDataDocument)
  const taxonomies = data?.contracts.taxonomies ?? []
  const { showDeleteModal, supplementDispatch } = useBulkImportSupplements()
  const { loading: loadingExpensables } = useExpensables()
  const { canImportSupplements } = usePayrollContextlessPolicies()
  const canSeeSupplements = policyAuthorizations.for('payroll_supplements.supplements.read')
  const didImportSupplements = supplementBulkImportCollection.length > 0

  // Note: No salary data in this view since there's no policy period context
  // Salary info requires a specific policy period to calculate estimated salaries

  const toggleEmployeeExpansion = (id: string) => {
    setEmployeeExpansionState((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleExpandAll = useMemo(
    () => () => {
      setEmployeeExpansionState((prev) => {
        const next = { ...prev }
        records.forEach((r: { id: number }) => {
          next[String(r.id)] = true
        })
        return next
      })
    },
    [recordsKey]
  )

  const handleCollapseAll = useMemo(
    () => () => {
      setEmployeeExpansionState((prev) => {
        const next = { ...prev }
        records.forEach((r: { id: number }) => {
          next[String(r.id)] = false
        })
        return next
      })
    },
    [recordsKey]
  )

  const allExpanded =
    records.length > 0 && records.every((r: { id: number }) => employeeExpansionState[String(r.id)])

  const loadingSupplements = loading || loadingExpensables || loadingData

  const sourceWithActions = useUnsafeMemo(() => {
    const expandCollapseAction = allExpanded
      ? {
          label: i18n.t('payroll_bundle.supplements.supplement_collapse_all'),
          onClick: handleCollapseAll
        }
      : {
          label: i18n.t('payroll_bundle.supplements.supplement_expand_all'),
          onClick: handleExpandAll
        }
    const secondaryActionsList = [
      expandCollapseAction,
      ...(canImportSupplements
        ? [
            {
              label: i18n.t('payroll_bundle.manage_imported_supplements.title'),
              onClick: () => supplementDispatch({ type: SHOW_MANAGED_IMPORTED_MODAL }),
              disabled: !didImportSupplements
            }
          ]
        : [])
    ]
    const primaryActions = canImportSupplements
      ? () => [
          {
            label: i18n.t('payroll_bundle.import_supplements.import.import_supplements'),
            onClick: () => {
              supplementDispatch({ type: SHOW_UPLOAD_MODAL })
              track(trackingEvents.payroll.userClicksOnPayrollImportSupplements)
            },
            disabled: loadingSupplements
          }
        ]
      : undefined
    // Explicitly preserve mappedVariables and document to maintain GraphqlDataCollectionSource type
    return {
      ...source,
      mappedVariables: source.mappedVariables,
      document: source.document,
      primaryActions,
      secondaryActions: () => secondaryActionsList
    }
  }, [
    source,
    canImportSupplements,
    handleExpandAll,
    handleCollapseAll,
    allExpanded,
    loadingSupplements,
    didImportSupplements,
    supplementDispatch
  ])

  return (
    <SupplementsInsightsContextProvider>
      <ExpensablesProvider supplementsStore={supplementsStore}>
        <ErrorBoundary>
          <SupplementExtraDataProvider
            supplementsStore={supplementsStore}
            loadingSupplementData={loading}
            date={date}
            frequency={frequency}
          >
            <StatusAuthorizationProvider
              employees={filteredEmployees}
              policyPeriod={null}
              policyPeriodStatus={undefined}
            >
              {canSeeSupplements && supplementsStore.forms.length > 0 && (
                <SupplementsInsights supplementsStore={supplementsStore} />
              )}
              <OneDataCollection
                id="payroll/supplements/employees-by-date/v1"
                source={sourceWithActions}
                visualizations={[
                  {
                    type: 'custom',
                    label: i18n.t('payroll_bundle.supplements.title'),
                    icon: List,
                    component: () => (
                      <SupplementsCustomVisualizationByDate
                        source={source}
                        employeesById={employeesById}
                        supplementsStore={supplementsStore}
                        loadingSupplements={loadingSupplements}
                        taxonomies={taxonomies}
                        itemToHighlight={null}
                        employeeExpansionState={employeeExpansionState}
                        toggleEmployeeExpansion={toggleEmployeeExpansion}
                        toggleTaxonomyCreationModal={handleTaxonomyCreationModal}
                      />
                    )
                  }
                ]}
              />
              {canImportSupplements && showDeleteModal && <DeleteModal store={supplementsStore} />}
              {openTaxonomyCreationModal && (
                <TaxonomyForm
                  legalEntities={legalEntities}
                  setModalOpen={handleTaxonomyCreationModal}
                />
              )}
            </StatusAuthorizationProvider>
          </SupplementExtraDataProvider>
        </ErrorBoundary>
      </ExpensablesProvider>
    </SupplementsInsightsContextProvider>
  )
}
